'use client'

import { ReactNode, useEffect, useState } from 'react'
import { Button, Cell, Dialog, Spacer, Text } from '@vinted/web-ui'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'

import { FaqEntryType } from 'constants/faq-entry'
import FaqEntryUrl from 'components/FaqEntryUrl'

import PersonalInformationSharingWarning from './PersonalInformationSharingWarning'
import { PersonalInformationSharingContentType } from '../../constants/personal-information-sharing'

const DISABLED_BUTTON_TIME_MS = 2500

type Props = {
  contentType: PersonalInformationSharingContentType
  onEditClick: () => void
  onSendClick: () => void
  onLearnMoreClick: () => void
}

const PersonalInformationSharingModalVariantB = ({
  contentType,
  onEditClick,
  onSendClick,
  onLearnMoreClick,
}: Props) => {
  const [isButtonEnabled, setIsButtonEnabled] = useState(false)

  const translate = useTranslate('conversation.personal_information_sharing_dialog')

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsButtonEnabled(true)
    }, DISABLED_BUTTON_TIME_MS)

    return () => clearTimeout(timer)
  }, [])

  const phishingExamplesLink = {
    'phishing-examples-link': (chunks: Array<ReactNode>) => (
      <FaqEntryUrl
        key="phishing-examples-link"
        type={FaqEntryType.SpamPhishing}
        render={faqUrl => (
          <a href={faqUrl} target="_blank" rel="noreferrer" onClick={onLearnMoreClick}>
            {chunks}
          </a>
        )}
      />
    ),
  }

  return (
    <Dialog contentDimensions={{ maxWidth: '343px' }} testId="email-sharing-modal" show>
      <Cell>
        <Text
          as="h2"
          type={Text.Type.Heading}
          text={
            contentType === PersonalInformationSharingContentType.Email
              ? translate('email.title')
              : translate('phone.title')
          }
          alignment={Text.Alignment.Center}
        />
        <Spacer size={Spacer.Size.Large} />
        <PersonalInformationSharingWarning contentType={contentType} />
        <Spacer size={Spacer.Size.Large} />
        <Text as="p" text={translate('protect_your_data', phishingExamplesLink)} />
        <Spacer size={Spacer.Size.X2Large} />
        <Button
          text={translate('actions.edit_message')}
          styling={Button.Styling.Filled}
          onClick={onEditClick}
          disabled={!isButtonEnabled}
          testId="email-sharing-modal--edit-button"
        />
        <Spacer size={Spacer.Size.Large} />
        <Button
          text={translate('actions.continue_at_your_own_risk')}
          styling={Button.Styling.Flat}
          onClick={onSendClick}
          disabled={!isButtonEnabled}
          testId="email-sharing-modal--send-button"
        />
      </Cell>
    </Dialog>
  )
}

export default PersonalInformationSharingModalVariantB
